import { useTranslation } from "react-i18next";
import {
    Button,
    Container,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldContent,
    FieldDescription,
    FieldError,
    FieldGroup,
    FieldLabel,
    FieldSet,
    Grid,
    Input,
    ListCard,
    ListCardRow,
    Mono,
    FilterBar,
    SectionHeader,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    StatCard,
    StatCardGroup,
    Switch,
    TableSkeleton,
    Text,
    Textarea,
} from "@athena/ui";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import type { OrganizationsQuery } from "../../graphql/organizations.generated";
import { useAllRows } from "@/hooks/useAllRows";
import {
    CreateOrganizationDocument,
    OrganizationChildrenDocument,
    OrganizationsDocument,
    UpdateOrganizationDocument,
} from "../../graphql/organizations.generated";
import { useEffect, useState } from "react";
import { ImageUploader } from "@/components/ImageUploader";
import { STATUS_LABEL } from "@/types/Status";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { OrgTable, orgColumns, type OrgRow } from "@/components/tables/OrgTable";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";

/** Sentinel for "no filter" — Radix Select has no unset value, only a chosen one. */
const ALL = "__all__";

const ORG_STATUSES = [
    "ACTIVE",
    "INACTIVE",
    "ARCHIVED",
] as const;

/**
 * Common shape shared by top-level orgs and lazily-fetched sub-orgs at any depth.
 *
 * Anchored on the nested `subOrganizations` selection rather than the top-level
 * `organizations` element — that's the narrowest of the shapes in play, so it's
 * exactly what both a top-level org (which has extra fields, fine for a superset)
 * and a lazily-fetched `OrganizationChildrenQuery["organization"]["subOrganizations"][number]`
 * (identical field selection) structurally satisfy. No `NonNullable` needed: every
 * field in this chain is a non-null list of non-null items in the schema.
 */
type OrgNode = OrganizationsQuery["organizations"][number]["subOrganizations"][number];

const emptyNewOrganization = {
    name: "",
    contactPhoneNumber: "",
    contactEmailAddress: "",
    // On by default, matching the column: a new partner takes crisis alerts unless somebody
    // says otherwise.
    crisisAlertsEnabled: true,
    emergencyContactEmail: "",
    emergencyContactPhone: "",
    chatEnabled: false,
    chatIntroMessage: "",
    outOfHoursMessage: "",
};

export function OrganizationsPage() {
    const [
        newOrganizationOpen,
        setNewOrganizationOpen,
    ] = useState<boolean>(false);
    const [
        newOrganization,
        setNewOrganization,
    ] = useState(emptyNewOrganization);
    const [
        creating,
        setCreating,
    ] = useState<boolean>(false);
    // Set once createOrganization succeeds — ImageUploader needs a real
    // organization id, which doesn't exist until the org is created, so the
    // first Save creates the org and subsequent edits (incl. the logo) update it.
    const [
        createdOrgId,
        setCreatedOrgId,
    ] = useState<string | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        statusFilter,
        setStatusFilter,
    ] = useState<string>(ALL);
    const settledSearch = useDebouncedValue(search);
    /**
     * Debounced while typing, immediate when emptied — the same rule the Users directory uses. A clear
     * is a discrete action, not a keystroke: reading the debounced value here left the Clear button and
     * the flat-list note on screen for a third of a second after the box was already empty, and kept
     * the flat query in flight that whole time.
     */
    const queryTerm = search.trim() === "" ? "" : settledSearch.trim();
    const filtersActive = queryTerm !== "" || statusFilter !== ALL;

    /**
     * Two shapes, chosen by whether anything is being narrowed.
     *
     * The tree is for browsing, and it loads lazily — only top-level orgs and their direct children
     * are on hand, deeper levels arrive when a row is expanded. Filtering that would search whatever
     * happened to be expanded and confidently report nothing for the rest, which is worse than not
     * offering search at all.
     *
     * So a search asks for every organization flat (`topLevelOnly: false`, which the resolver already
     * answers with the caller's whole accessible set) and shows a flat list of matches. Clearing the
     * search returns to the tree.
     */
    /*
     * Every organization, not the first fifty.
     *
     * This page searches, builds a tree and counts over the array it holds, so a truncated read
     * did not just hide rows — the stat cards below reported the size of the page as the number of
     * organizations. Ninety fit in a single request; `useAllRows` keeps that true if they grow.
     */
    const {
        rows: organizations,
        loading,
        refetch,
    } = useAllRows<OrganizationsQuery["organizations"][number]>(
        OrganizationsDocument,
        (d) => (d as OrganizationsQuery | undefined)?.organizations,
        { variables: { topLevelOnly: !filtersActive } },
    );
    const [createOrganization] = useMutation(CreateOrganizationDocument);
    const [updateOrganization] = useMutation(UpdateOrganizationDocument);

    const [
        expandedOrgIds,
        setExpandedOrgIds,
    ] = useState<Set<string>>(new Set());
    // Children fetched lazily as deeper rows are expanded (depth-1 children
    // arrive with the top-level orgs already, so this only fills in past that).
    const [
        childrenByOrgId,
        setChildrenByOrgId,
    ] = useState<Map<string, OrgNode[]>>(new Map());
    const [
        loadingChildIds,
        setLoadingChildIds,
    ] = useState<Set<string>>(new Set());

    const [fetchChildren] = useLazyQuery(OrganizationChildrenDocument);

    // Seed depth-1 children from the top-level query so expanding a top-level
    // org doesn't need a network round-trip — only deeper expansions do.
    useEffect(() => {
        if (organizations.length === 0) return;
        setChildrenByOrgId((prev) => {
            const next = new Map(prev);
            for (const org of organizations) next.set(org.id, org.subOrganizations);
            return next;
        });
    }, [organizations]);

    const childrenOf = (org: OrgNode): OrgNode[] | undefined => childrenByOrgId.get(org.id);

    const toggleExpanded = async (org: OrgNode) => {
        const next = new Set(expandedOrgIds);
        if (next.has(org.id)) {
            next.delete(org.id);
            setExpandedOrgIds(next);
            return;
        }
        next.add(org.id);
        setExpandedOrgIds(next);

        if (!childrenByOrgId.has(org.id)) {
            setLoadingChildIds((prev) => new Set(prev).add(org.id));
            const { data: childData } = await fetchChildren({ variables: { id: org.id } });
            setChildrenByOrgId((prev) => new Map(prev).set(org.id, childData?.organization?.subOrganizations ?? []));
            setLoadingChildIds((prev) => {
                const copy = new Set(prev);
                copy.delete(org.id);
                return copy;
            });
        }
    };

    /** Flattens the expanded tree into visible { org, depth } rows for the table, alphabetized at every level. */
    const flattenVisible = (nodes: OrgNode[], depth: number): { org: OrgNode; depth: number }[] =>
        [...nodes]
            .sort((a, b) => a.name.localeCompare(b.name))
            .flatMap((org) => {
                const row = { org, depth };
                if (!expandedOrgIds.has(org.id)) return [row];
                return [
                    row,
                    ...flattenVisible(childrenOf(org) ?? [], depth + 1),
                ];
            });

    /**
     * Flat while narrowing — nesting a filtered result is meaningless, since a match's parent may
     * not itself match and would have to be shown as a row that does not belong to the answer.
     */
    const orgRows = (
        filtersActive
            ? organizations
                  .filter((org) => {
                      if (statusFilter !== ALL && org.status !== statusFilter) return false;
                      const term = queryTerm.toLowerCase();
                      if (!term) return true;
                      // Slug and contact email as well as the name: an operator hunting a partner often
                      // has the address off an email thread rather than the display name.
                      return `${org.name} ${org.slug ?? ""} ${org.contactEmailAddress ?? ""}`
                          .toLowerCase()
                          .includes(term);
                  })
                  .map((org) => ({ org, depth: 0 }))
            : flattenVisible(organizations, 0)
    ).map(({ org, depth }) => ({
        ...org,
        depth,
        memberCount: org.userCount,
        appUserCount: org.appUserCount,
        // The row's own children, not the lazily-fetched map: a row whose children have not been
        // loaded yet still needs an expander, which is the whole point of loading them on demand.
        hasChildren: org.subOrganizations.length > 0,
    }));

    /*
     * Scroll or pages, the reader's choice, portal-wide — see `useListView`.
     *
     * Applied to the flattened rows, so an expanded sub-org is paged with the parent it is nested
     * under rather than as a list of its own.
     */
    const view = useListView(orgRows);

    // Keyed on the rows, not `loading`: Apollo sets loading on every refetch, so gating
    // the whole page on it replaces live content — and unmounts any child holding
    // state or an in-flight request — every time something reloads.
    if (loading && organizations.length === 0) return <TableSkeleton />;

    const handleRowClick = (id: string) => {
        navigate(`/organizations/${id}`);
    };

    /**
     * An organization taking crisis alerts needs somewhere to send them — one contact is enough.
     * With alerts off neither is required, which is the point of the toggle.
     */
    const crisisContactMissing =
        newOrganization.crisisAlertsEnabled &&
        !newOrganization.emergencyContactEmail.trim() &&
        !newOrganization.emergencyContactPhone.trim();

    const closeNewOrganizationDialog = () => {
        setNewOrganizationOpen(false);
        setNewOrganization(emptyNewOrganization);
        setCreatedOrgId(null);
    };

    /**
     * Refreshes the list after a write, swallowing a failed refetch.
     *
     * The write it follows has already succeeded, so a stale list is the whole cost — and
     * `handleCreate` is invoked as `void handleCreate()`, which turns any rejection here into an
     * unhandled promise rejection rather than something a user ever sees. Apollo aborts an
     * in-flight query when the component unmounts, so navigating away right after saving was
     * enough to produce one.
     */
    const refreshList = async () => {
        try {
            await refetch();
        } catch {
            // Deliberately ignored — see above.
        }
    };

    const handleCreate = async () => {
        if (!newOrganization.name.trim() || crisisContactMissing) return;
        setCreating(true);
        try {
            if (!createdOrgId) {
                const { data: createData } = await createOrganization({
                    variables: {
                        input: {
                            name: newOrganization.name.trim(),
                            contactPhoneNumber: newOrganization.contactPhoneNumber.trim() || undefined,
                            contactEmailAddress: newOrganization.contactEmailAddress.trim() || undefined,
                            crisisAlertsEnabled: newOrganization.crisisAlertsEnabled,
                            emergencyContactEmail: newOrganization.emergencyContactEmail.trim() || undefined,
                            emergencyContactPhone: newOrganization.emergencyContactPhone.trim() || undefined,
                            chatEnabled: newOrganization.chatEnabled,
                            chatIntroMessage: newOrganization.chatIntroMessage.trim() || undefined,
                            outOfHoursMessage: newOrganization.outOfHoursMessage.trim() || undefined,
                        },
                    },
                });
                const id = createData?.createOrganization.id;
                if (id) setCreatedOrgId(id);
                await refreshList();
                return;
            }

            await updateOrganization({
                variables: {
                    id: createdOrgId,
                    input: {
                        name: newOrganization.name.trim(),
                        contactPhoneNumber: newOrganization.contactPhoneNumber.trim() || undefined,
                        contactEmailAddress: newOrganization.contactEmailAddress.trim() || undefined,
                        crisisAlertsEnabled: newOrganization.crisisAlertsEnabled,
                        emergencyContactEmail: newOrganization.emergencyContactEmail.trim() || null,
                        emergencyContactPhone: newOrganization.emergencyContactPhone.trim() || null,
                        chatEnabled: newOrganization.chatEnabled,
                        chatIntroMessage: newOrganization.chatIntroMessage.trim() || undefined,
                        outOfHoursMessage: newOrganization.outOfHoursMessage.trim() || undefined,
                    },
                },
            });
            await refreshList();
            closeNewOrganizationDialog();
        } finally {
            setCreating(false);
        }
    };

    const handleLogoUploaded = (key: string) => {
        if (!createdOrgId) return;
        void updateOrganization({ variables: { id: createdOrgId, input: { logoUrl: key } } });
    };

    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow={t("admin.orgs.managePartners")}
                title={t("nav.organizations")}
                description={t("admin.orgs.subTitle")}
                actions={
                    <Button
                        size="sm"
                        className="flex items-center gap-1.5"
                        onClick={() => setNewOrganizationOpen(true)}
                    >
                        <Plus className="h-3.5 w-3.5" />
                        {t("admin.orgs.add")}
                    </Button>
                }
            />
            <StatCardGroup className="mb-4 w-full">
                <StatCard
                    label={filtersActive ? "Matching" : t("admin.partners")}
                    value={loading ? "—" : String(orgRows.length)}
                />
                <StatCard
                    label={t("admin.users")}
                    value={loading ? "—" : organizations.reduce((sum, o) => sum + o.userCount, 0).toLocaleString()}
                />
                <StatCard
                    label="App users"
                    value={loading ? "—" : organizations.reduce((sum, o) => sum + o.appUserCount, 0).toLocaleString()}
                />
                <StatCard
                    label={t("admin.chatEnabled")}
                    value={loading ? "—" : String(organizations.filter((o) => o.chatEnabled).length)}
                />
            </StatCardGroup>
            <FilterBar
                search={search}
                onSearchChange={setSearch}
                searchLabel="Search organizations"
                searchPlaceholder="Search by name, slug or contact email"
                active={filtersActive}
                onClear={() => {
                    setSearch("");
                    setStatusFilter(ALL);
                }}
                note="Showing a flat list of every matching organization."
            >
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" aria-label="Filter by status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All statuses</SelectItem>
                        {ORG_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                                {STATUS_LABEL[status]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </FilterBar>

            <ListViewControls view={view} noun="organizations" />

            <OrgTable
                columns={[
                    orgColumns.identity((org: OrgRow) => {
                        // Only a nested list has expanders, and only a row that has children.
                        const hasChildren = !filtersActive && !!org.hasChildren;
                        if (!hasChildren) return <div className="w-5 shrink-0" />;
                        const isExpanded = expandedOrgIds.has(org.id);
                        return (
                            <button
                                type="button"
                                aria-label={isExpanded ? "Collapse sub-orgs" : "Expand sub-orgs"}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void toggleExpanded(org as never);
                                }}
                                className="text-muted-foreground hover:text-foreground flex h-5 w-5 shrink-0 items-center justify-center transition-colors"
                            >
                                {loadingChildIds.has(org.id) ? (
                                    <Mono size="xs">…</Mono>
                                ) : isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                ) : (
                                    <ChevronRight className="h-4 w-4" />
                                )}
                            </button>
                        );
                    }),
                    orgColumns.contactEmail,
                    orgColumns.members,
                    orgColumns.appUsers,
                    orgColumns.status,
                    orgColumns.chatEnabled,
                ]}
                rows={view.rows}
                rowKey={(org: OrgRow) => org.id}
                onRowClick={(org: OrgRow) => handleRowClick(org.id)}
                loading={loading}
                filtered={filtersActive}
                noMatches="No organizations match this search."
            />
            <Dialog
                open={newOrganizationOpen}
                onOpenChange={(open) => {
                    if (open) setNewOrganizationOpen(true);
                    else closeNewOrganizationDialog();
                }}
            >
                <DialogContent className="flex max-h-[90vh] max-w-7xl flex-col gap-4 overflow-y-auto">
                    <DialogHeader className="">
                        <DialogTitle>{t("common.fields.newOrg")}</DialogTitle>
                    </DialogHeader>
                    <Grid cols={2}>
                        <ListCard
                            className="shrink-0"
                            title="Basic Information"
                            description="Essential information about the organization"
                        >
                            <ListCardRow className="max-h-max">
                                <FieldSet className="w-full max-w-none">
                                    <FieldGroup>
                                        <Field>
                                            <FieldLabel htmlFor="org-name" required>
                                                {t("common.fields.orgName")}
                                            </FieldLabel>
                                            <Input
                                                required
                                                id="org-name"
                                                value={newOrganization.name}
                                                onChange={(e) =>
                                                    setNewOrganization((prev) => ({ ...prev, name: e.target.value }))
                                                }
                                                type="text"
                                            />
                                        </Field>
                                        <Field>
                                            <FieldLabel htmlFor="contact-email">Contact Email Address</FieldLabel>
                                            <Input
                                                id="contact-email"
                                                type="email"
                                                value={newOrganization.contactEmailAddress}
                                                onChange={(e) =>
                                                    setNewOrganization((prev) => ({
                                                        ...prev,
                                                        contactEmailAddress: e.target.value,
                                                    }))
                                                }
                                                placeholder="email@example.com"
                                            />
                                        </Field>
                                        <Field>
                                            <FieldLabel htmlFor="contact-phone">Contact Phone Number</FieldLabel>
                                            <Input
                                                id="contact-phone"
                                                type="text"
                                                value={newOrganization.contactPhoneNumber}
                                                onChange={(e) =>
                                                    setNewOrganization((prev) => ({
                                                        ...prev,
                                                        contactPhoneNumber: e.target.value,
                                                    }))
                                                }
                                                placeholder="(555) 555-5555"
                                            />
                                        </Field>
                                        {/*
                                         * Two-phase by design: an image needs a real organization
                                         * id to belong to, so the logo field only appears once the
                                         * org exists. The uploader is the whole field — the dashed
                                         * well that used to be here is now its own.
                                         */}
                                        <Field>
                                            <FieldLabel>{t("common.fields.orgLogo")}</FieldLabel>
                                            {createdOrgId ? (
                                                <ImageUploader
                                                    organizationId={createdOrgId}
                                                    onUploaded={handleLogoUploaded}
                                                    label="Upload logo"
                                                    hint="Shown to members. PNG, JPEG, WebP, or GIF, up to 5 MB. Or drag one here."
                                                />
                                            ) : (
                                                <Text size="sm" muted>
                                                    Save the organization first, then add a logo.
                                                </Text>
                                            )}
                                        </Field>
                                    </FieldGroup>
                                </FieldSet>
                            </ListCardRow>
                        </ListCard>
                        <ListCard
                            className="shrink-0"
                            title="Chat Settings"
                            description="Configure chat functionality for the organization"
                        >
                            <ListCardRow>
                                <Field orientation="horizontal" className="flex max-w-lg">
                                    <Switch
                                        id="enable-chat"
                                        checked={newOrganization.chatEnabled}
                                        onCheckedChange={(checked) =>
                                            setNewOrganization((prev) => ({ ...prev, chatEnabled: checked }))
                                        }
                                    />
                                    <FieldContent>
                                        <FieldLabel htmlFor="enable-chat">{t("common.labels.enableChat")}</FieldLabel>
                                        <FieldDescription className="text-text-body text-xs">
                                            Allow members of this organization to use the coaching chat feature.
                                        </FieldDescription>
                                    </FieldContent>
                                </Field>
                            </ListCardRow>
                            <ListCardRow>
                                <Field orientation="horizontal" className="flex max-w-lg">
                                    <Switch
                                        id="enable-crisis-alerts"
                                        checked={newOrganization.crisisAlertsEnabled}
                                        onCheckedChange={(checked) =>
                                            setNewOrganization((prev) => ({ ...prev, crisisAlertsEnabled: checked }))
                                        }
                                    />
                                    <FieldContent>
                                        <FieldLabel htmlFor="enable-crisis-alerts">Crisis Alerts</FieldLabel>
                                        <FieldDescription className="text-text-body text-xs">
                                            Notify this organization when a member&rsquo;s message trips a crisis
                                            keyword. Turn it off for a partner who does not want to be involved in
                                            crisis triggers — members here still receive the crisis response message,
                                            and our own team is still alerted.
                                        </FieldDescription>
                                    </FieldContent>
                                </Field>
                            </ListCardRow>
                            <ListCardRow className="flex flex-col">
                                <Field>
                                    <FieldLabel htmlFor="crisis-email">{t("common.fields.crisisEmail")}</FieldLabel>
                                    <FieldDescription className="text-text-body text-xs">
                                        This is the email address that will get notified when a Crisis Alert is
                                        triggered.
                                        {newOrganization.crisisAlertsEnabled
                                            ? " It is the only contact an alert is actually delivered to today."
                                            : " Not used while Crisis Alerts are off."}
                                    </FieldDescription>
                                    <Input
                                        id="crisis-email"
                                        type="email"
                                        value={newOrganization.emergencyContactEmail}
                                        disabled={!newOrganization.crisisAlertsEnabled}
                                        onChange={(e) =>
                                            setNewOrganization((prev) => ({
                                                ...prev,
                                                emergencyContactEmail: e.target.value,
                                            }))
                                        }
                                        placeholder="email@example.com"
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel htmlFor="crisis-phone">{t("common.fields.crisisPhone")}</FieldLabel>
                                    <FieldDescription className="text-text-body text-xs">
                                        Kept on file as a crisis contact. Nothing dials it yet, so an organization with
                                        only a phone number here is not notified in practice.
                                    </FieldDescription>
                                    <Input
                                        id="crisis-phone"
                                        type="text"
                                        value={newOrganization.emergencyContactPhone}
                                        disabled={!newOrganization.crisisAlertsEnabled}
                                        onChange={(e) =>
                                            setNewOrganization((prev) => ({
                                                ...prev,
                                                emergencyContactPhone: e.target.value,
                                            }))
                                        }
                                        placeholder="(555) 555-5555"
                                    />
                                </Field>
                                {crisisContactMissing && (
                                    <FieldError>
                                        Add a crisis alert email or phone number, or turn Crisis Alerts off.
                                    </FieldError>
                                )}
                            </ListCardRow>
                            <ListCardRow>
                                <FieldSet className="w-full max-w-none">
                                    <FieldGroup>
                                        <Field>
                                            <FieldLabel htmlFor="custom-chat-intro">
                                                Custom Chat Intro Message
                                            </FieldLabel>
                                            <FieldDescription className="text-text-body text-xs">
                                                Sent as the first message when a user starts a new chat. Falls back to a
                                                platform default when unset.
                                            </FieldDescription>
                                            <Textarea
                                                id="custom-chat-intro"
                                                placeholder="Enter a custom welcome message for users starting a chat..."
                                                rows={4}
                                                maxLength={500}
                                                value={newOrganization.chatIntroMessage}
                                                onChange={(e) =>
                                                    setNewOrganization((prev) => ({
                                                        ...prev,
                                                        chatIntroMessage: e.target.value,
                                                    }))
                                                }
                                            />
                                            <Mono size="xs">
                                                {newOrganization.chatIntroMessage.length}/500 characters
                                            </Mono>
                                        </Field>
                                    </FieldGroup>
                                </FieldSet>
                            </ListCardRow>
                            <ListCardRow>
                                <FieldSet className="w-full max-w-none">
                                    <FieldGroup>
                                        <Field>
                                            <FieldLabel htmlFor="out-of-hours-message">Out-of-Hours Message</FieldLabel>
                                            <FieldDescription className="text-text-body text-xs">
                                                Sent once per out-of-hours session when a user messages outside business
                                                hours (Mon–Fri, 10am–8pm ET). Falls back to a platform default when
                                                unset.
                                            </FieldDescription>
                                            <Textarea
                                                id="out-of-hours-message"
                                                placeholder="Enter a custom out-of-hours auto-response..."
                                                rows={4}
                                                maxLength={500}
                                                value={newOrganization.outOfHoursMessage}
                                                onChange={(e) =>
                                                    setNewOrganization((prev) => ({
                                                        ...prev,
                                                        outOfHoursMessage: e.target.value,
                                                    }))
                                                }
                                            />
                                            <Mono size="xs">
                                                {newOrganization.outOfHoursMessage.length}/500 characters
                                            </Mono>
                                        </Field>
                                    </FieldGroup>
                                </FieldSet>
                            </ListCardRow>
                        </ListCard>
                    </Grid>
                    <DialogFooter>
                        <Button variant={"outline"} onClick={closeNewOrganizationDialog}>
                            {createdOrgId ? t("common.actions.done") : t("common.actions.cancel")}
                        </Button>
                        <Button
                            disabled={!newOrganization.name.trim() || creating || crisisContactMissing}
                            onClick={() => void handleCreate()}
                        >
                            {creating ? "Saving…" : createdOrgId ? "Save changes" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Container>
    );
}
